import { ParentRepository } from '@/repository/ParentRepository'
import { TeacherRepository } from '@/repository/TeacherRepository'
import { UserAuth } from '@/auth/user'

import { UserAlreadyExistsError, UserNotFoundError, UserUnAuthorizedError, UserUnVerifiedError, InvalidEmail, WeakPasswordError } from './errors'

class AuthService {
  constructor(
    authClient,
    dbClient
  ) {
    if (!(authClient)) {
      throw new Error(`'authClient' is required.`)
    }
    this.auth = new UserAuth(authClient)
    if (dbClient) {
      this.dbClient = dbClient
      this.parentRepo = new ParentRepository(dbClient)
      this.teacherRepo = new TeacherRepository(dbClient)
    }
  }

  async signInAsParent(email, password) {
    if (!(typeof email === 'string')) {
      throw new TypeError(`The type of 'email' must be String. Actual: ${typeof email}`)
    }
    if (!(typeof password === 'string')) {
      throw new TypeError(`The type of 'password' must be String. Actual: ${typeof password}`)
    }
    const credential = await this.signIn(email, password)
    let parent
    if (parent = await this.parentRepo.find(credential.user.uid)) {
      this._isVerified(credential)
      return parent
    } else if (await this.teacherRepo.find(credential.user.uid)) {
      throw new UserNotFoundError(`指定されたユーザーは見つかりません`)
    } else {
      this._isVerified(credential)
      return null
    }
  }

  async signInAsTeacher(email, password) {
    if (!(typeof email === 'string')) {
      throw new TypeError(`The type of 'email' must be String. Actual: ${typeof email}`)
    }
    if (!(typeof password === 'string')) {
      throw new TypeError(`The type of 'password' must be String. Actual: ${typeof password}`)
    }
    const credential = await this.signIn(email, password)
    let teacher
    if (teacher = await this.teacherRepo.find(credential.user.uid)) {
      this._isVerified(credential)
      return teacher
    } else if (await this.parentRepo.find(credential.user.uid)) {
      throw new UserNotFoundError(`指定されたユーザーは見つかりません`)
    } else {
      this._isVerified(credential)
      return null
    }
  }

  async signIn(email, password) {
    try {
      return await this.auth.signIn(email, password)
    } catch (e) {
      switch (e.code) {
      case "auth/invalid-email":
        throw new UserUnAuthorizedError(`メールアドレスが正しくありません`)
      case "auth/user-not-found":
        throw new UserNotFoundError(`指定されたユーザーは見つかりません`)
      case "auth/wrong-password":
        throw new UserUnAuthorizedError(`パスワードが正しくありません`)
      default:
        throw new Error(e.message)
      }
    }
  }

  async createUser(email, password, url) {
    try {
      const user = await this.auth.createUser(email, password)
      await this.sendVerifyMail(url)
      return user
    } catch (e) {
      switch (e.code) {
      case "auth/email-already-in-use":
        throw new UserAlreadyExistsError(`このメールアドレスは既に存在しています`)
      case "auth/invalid-email":
        throw new InvalidEmail(`メールアドレスが不適切です`)
      case "auth/weak-password":
        throw new WeakPasswordError(`危険なパスワードです`)
      default:
        throw new Error(e)
      }
    }
  }

  _isVerified(credential) {
    if (!credential.user.emailVerified) {
      throw new UserUnVerifiedError(`メールアドレスの確認が終わっていません`)
    }
  }

  async sendVerifyMail(url) {
    if (!(typeof url === 'string')) {
      throw new TypeError(`The type of 'url' must be String. Actual: ${typeof url}`)
    }
    await this.auth.sendEmailVerification(url)
  }

  async resendVerifyMail(email, password, url) {
    if (!(typeof email === 'string')) {
      throw new TypeError(`The type of 'email' must be String. Actual: ${typeof email}`)
    }
    if (!(typeof password === 'string')) {
      throw new TypeError(`The type of 'password' must be String. Actual: ${typeof password}`)
    }
    if (!(typeof url === 'string')) {
      throw new TypeError(`The type of 'url' must be String. Actual: ${typeof url}`)
    }
    return await this.createUser(email, password, url)
  }

  async sendPasswordResetMail(email) {
    if (!(typeof email === 'string')) {
      throw new TypeError(`The type of 'email' must be String. Actual: ${typeof email}`)
    }
    try {
      return await this.auth.sendPasswordResetEmail(email)
    } catch (e) {
      switch (e.code) {
      case "auth/invalid-email":
        throw new InvalidEmail(`不適切なメールアドレスです`)
      case "auth/user-not-found":
        throw new UserNotFoundError(`指定されたユーザーは見つかりません`)
      default:
        throw new Error(e.message)
      }
    }
  }

  async deleteUser(email, password) {
    return await this.client.deleteUser(email, password)
  }

}


export { AuthService }